import { Injectable, InjectionToken, computed, inject, signal } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { Observable, Subject } from 'rxjs';
import { ENVIRONMENT } from 'shared';

/**
 * WolfPack Tracking Engine V2 — the app's single place that knows the visitor's
 * cookie consent. Binding contract: docs/tracking/EVENT_CONTRACT_V2.md.
 *
 * - The decision lives in the first-party cookie `wp_consent`, which the landing
 *   SDK (wp-consent.js) writes on the SAME host in the SAME format: a decision made
 *   on the landing page is honoured here without a second banner, and vice versa.
 * - `wp_vid` (visitor id) and `wp_sid` (session id + last activity) exist only
 *   while `analytics` is granted; any grant without it deletes both at once.
 * - The brand configuration (`policy_version`, `pre_consent_mode`, ...) comes from
 *   `GET /api/t/c` with plain `fetch`, never HttpClient — the app's interceptors
 *   would turn an error of that endpoint into a redirect to /login or the
 *   maintenance screen. It is loaded lazily on the first `ready()`, so an app that
 *   never shows the banner and never tracks (mnlo, nam3less) makes no request.
 * - The service never sends anything itself; TrackingService listens to
 *   `changes$` and reports `consent_change`.
 */

export type ConsentCategory = 'necessary' | 'analytics' | 'marketing';
export type ConsentState = 'none' | 'necessary' | 'analytics' | 'marketing';
export type ConsentAction = 'accept_all' | 'necessary_only' | 'custom' | 'withdraw' | 'reconfirm';
export type ConsentSurface = 'app_banner' | 'app_preferences';
export type PreConsentMode = 'strict' | 'cookieless';

export const CONSENT_CATEGORIES: readonly ConsentCategory[] = ['necessary', 'analytics', 'marketing'];

/** Response of `GET /api/t/c` (contract §7). */
export interface ConsentRemoteConfig {
  config_domain_id: number | null;
  policy_version: string;
  pre_consent_mode: PreConsentMode;
  categories: ConsentCategory[];
  cookie_days: number;
  /** Optional: a brand-specific cookie policy URL, if the backend ever sends one. */
  policy_url?: string | null;
  /**
   * Languages the banner and preference center may be shown in (customer decisions
   * A2/P7, 2026-10-12): only these count as reviewed. Default `['en']`.
   */
  banner_languages: string[];
  /** Language shown whenever the visitor's language is not listed. Default `'en'`. */
  master_language: string;
}

/** Content of the `wp_consent` cookie (contract §2). */
export interface ConsentRecord {
  v: 1;
  id: string;
  at: string;
  c: ConsentCategory[];
  pv: string;
}

export interface ConsentChange {
  previous: ConsentRecord | null;
  /** Consent state the previous record had under the current policy version. */
  previousState: ConsentState;
  record: ConsentRecord;
  state: ConsentState;
  action: ConsentAction;
  surface: ConsentSurface;
  policyVersion: string;
}

/**
 * Per-app switch, passed through `createAppConfig({ consent: {...} })`.
 * Without it (or with `enabled: false`) no banner is rendered.
 */
export interface ConsentAppConfig {
  enabled: boolean;
  /** Tracking page id of the app outside its checkout (`gh-app`, `f3-app`, ...). */
  pageId?: string;
  /** Policy document the banner links to (`/api/media/<lang>/policy/<key>`), default `privacy`. */
  policyKey?: string;
}

export const CONSENT_APP_CONFIG = new InjectionToken<ConsentAppConfig>('CONSENT_APP_CONFIG');

export const CONSENT_COOKIE = 'wp_consent';
export const VISITOR_COOKIE = 'wp_vid';
export const SESSION_COOKIE = 'wp_sid';
/** `wp_sid` ends after 30 minutes without activity. */
export const SESSION_IDLE_SECONDS = 30 * 60;

const UUID_V4 = /^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/;
const FETCH_TIMEOUT_MS = 5000;

export const FALLBACK_CONSENT_CONFIG: ConsentRemoteConfig = {
  config_domain_id: null,
  policy_version: 'unknown',
  pre_consent_mode: 'strict',
  categories: ['necessary', 'analytics', 'marketing'],
  cookie_days: 365,
  banner_languages: ['en'],
  master_language: 'en'
};

const LANG_TAG = /^[A-Za-z]{2,3}(-[A-Za-z0-9]{2,8})?$/;

/**
 * Language the consent texts are shown in (customer decisions A2/P7): the visitor's
 * language only if `listed` contains it — exactly (`es-MX`) or, for a regional
 * variant, its base (`es-MX` → `es`) — otherwise `master`. Returns the listed
 * spelling. An unlisted language is never shown, whatever translations exist.
 * Same rule as the landing module (wp-consent.js `bannerLang`).
 */
export function resolveBannerLanguage(lang: string | null | undefined, listed: readonly string[] | null | undefined,
                                      master: string | null | undefined): string {
  const fallback = typeof master === 'string' && LANG_TAG.test(master) ? master : 'en';
  const list = (listed ?? []).filter(l => typeof l === 'string' && LANG_TAG.test(l));
  const want = String(lang ?? '').trim();
  if (!want) return fallback;
  const lower = want.toLowerCase();
  const exact = list.find(l => l.toLowerCase() === lower);
  if (exact) return exact;
  const base = lower.split('-')[0];
  if (base !== lower) {
    const b = list.find(l => l.toLowerCase() === base);
    if (b) return b;
  }
  return fallback;
}

function cleanLanguages(value: unknown, master: string): string[] {
  const list = Array.isArray(value)
    ? (value as unknown[]).filter((x): x is string => typeof x === 'string' && LANG_TAG.test(x.trim())).map(x => x.trim())
    : [];
  if (!list.length) list.push(master);
  return Array.from(new Set(list));
}

export function consentUuid(): string {
  try {
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') return crypto.randomUUID();
  } catch { /* fall through */ }
  const b = new Uint8Array(16);
  try { crypto.getRandomValues(b); } catch { for (let i = 0; i < 16; i++) b[i] = Math.floor(Math.random() * 256); }
  b[6] = (b[6] & 0x0f) | 0x40;
  b[8] = (b[8] & 0x3f) | 0x80;
  const h = Array.from(b, x => x.toString(16).padStart(2, '0')).join('');
  return `${h.slice(0, 8)}-${h.slice(8, 12)}-${h.slice(12, 16)}-${h.slice(16, 20)}-${h.slice(20)}`;
}

export function isUuidV4(value: unknown): value is string {
  return typeof value === 'string' && UUID_V4.test(value);
}

/** Highest state the categories allow. Marketing without analytics is not a valid choice. */
export function consentStateOf(categories: readonly ConsentCategory[] | null | undefined): ConsentState {
  if (!categories) return 'none';
  if (categories.includes('analytics') && categories.includes('marketing')) return 'marketing';
  if (categories.includes('analytics')) return 'analytics';
  return 'necessary';
}

/** Necessary always on; marketing only together with analytics; only offered categories. */
export function normalizeCategories(categories: readonly string[], offered: readonly string[] = CONSENT_CATEGORIES): ConsentCategory[] {
  const want = new Set(categories);
  const result: ConsentCategory[] = ['necessary'];
  if (want.has('analytics') && offered.includes('analytics')) result.push('analytics');
  if (want.has('marketing') && offered.includes('marketing') && result.includes('analytics')) result.push('marketing');
  return result;
}

// ── cookie helpers (host-only, Path=/, SameSite=Lax, Secure on https) ────────

export function readCookie(name: string): string | null {
  try {
    if (typeof document === 'undefined') return null;
    const prefix = `${name}=`;
    for (const part of document.cookie.split(';')) {
      const p = part.trim();
      if (p.startsWith(prefix)) return p.slice(prefix.length);
    }
  } catch { /* ignore */ }
  return null;
}

/** `maxAgeSeconds` null = session cookie, 0 = delete. */
export function writeCookie(name: string, value: string, maxAgeSeconds: number | null): void {
  try {
    if (typeof document === 'undefined') return;
    let c = `${name}=${value}; Path=/; SameSite=Lax`;
    if (maxAgeSeconds !== null) c += `; Max-Age=${Math.max(0, Math.floor(maxAgeSeconds))}`;
    if (typeof location !== 'undefined' && location.protocol === 'https:') c += '; Secure';
    document.cookie = c;
  } catch { /* ignore */ }
}

export function deleteCookie(name: string): void {
  writeCookie(name, '', 0);
}

function parseRecord(raw: string | null): ConsentRecord | null {
  if (!raw) return null;
  try {
    const o = JSON.parse(decodeURIComponent(raw));
    if (!o || typeof o !== 'object' || !isUuidV4(o.id) || !Array.isArray(o.c) || typeof o.pv !== 'string') return null;
    return {
      v: o.v, id: o.id, at: String(o.at ?? ''), pv: o.pv,
      c: (o.c as unknown[]).filter((x): x is ConsentCategory => (CONSENT_CATEGORIES as readonly unknown[]).includes(x))
    };
  } catch { return null; }
}

@Injectable({ providedIn: 'root' })
export class ConsentService {
  private environment: any = inject(ENVIRONMENT, { optional: true });
  readonly appConfig: ConsentAppConfig = inject(CONSENT_APP_CONFIG, { optional: true }) ?? { enabled: false };

  private readonly endpoint: string;
  private loading: Promise<ConsentRemoteConfig> | null = null;

  /** Brand configuration; null until `ready()` resolved. */
  readonly config = signal<ConsentRemoteConfig | null>(null);
  /** The stored `wp_consent` record as read from the cookie, valid or not. */
  readonly record = signal<ConsentRecord | null>(parseRecord(readCookie(CONSENT_COOKIE)));
  /** Whether the preference center is open (rendered by the app's consent overlay). */
  readonly preferencesOpen = signal(false);
  /**
   * Number of rendered "Cookie settings" links (e.g. in the layout's sidebar
   * footer). While none is on screen the consent overlay shows its own small
   * floating link, so the preference center is reachable on every page.
   */
  readonly footerLinks = signal(0);

  private readonly effectiveConfig = computed(() => this.config() ?? FALLBACK_CONSENT_CONFIG);

  /** The record, if it still counts: right format version and current policy version. */
  readonly validRecord = computed<ConsentRecord | null>(() => {
    const r = this.record();
    if (!r || r.v !== 1) return null;
    return r.pv === this.effectiveConfig().policy_version ? r : null;
  });

  readonly state = computed<ConsentState>(() => {
    const r = this.validRecord();
    return r ? consentStateOf(r.c) : 'none';
  });

  /** True once the brand config is known and there is no valid decision. */
  readonly needsDecision = computed(() => this.config() !== null && this.validRecord() === null);
  readonly categories = computed<ConsentCategory[]>(() => this.validRecord()?.c ?? []);
  readonly analyticsGranted = computed(() => this.state() === 'analytics' || this.state() === 'marketing');
  readonly marketingGranted = computed(() => this.state() === 'marketing');

  readonly state$: Observable<ConsentState> = toObservable(this.state);

  private readonly changeSubject = new Subject<ConsentChange>();
  /** Emits after every decision, the cookies are already written. */
  readonly changes$: Observable<ConsentChange> = this.changeSubject.asObservable();

  constructor() {
    const base = String(this.environment?.serverUrl ?? '/api').replace(/\/+$/, '');
    this.endpoint = `${base}/t/c`;
  }

  /** Consent id of the valid decision (null without one). */
  get id(): string | null { return this.validRecord()?.id ?? null; }
  get policyVersion(): string { return this.effectiveConfig().policy_version; }
  /** Language the consent texts are shown in for `lang` (see `resolveBannerLanguage`). */
  bannerLanguageFor(lang: string | null | undefined): string {
    const cfg = this.effectiveConfig();
    return resolveBannerLanguage(lang, cfg.banner_languages, cfg.master_language);
  }
  get preConsentMode(): PreConsentMode { return this.effectiveConfig().pre_consent_mode; }
  get offeredCategories(): ConsentCategory[] {
    const offered = this.effectiveConfig().categories?.filter(c => (CONSENT_CATEGORIES as readonly string[]).includes(c));
    return offered?.length ? offered : [...CONSENT_CATEGORIES];
  }

  /** Loads the brand configuration once; never rejects (fallback: strict, policy "unknown"). */
  ready(): Promise<ConsentRemoteConfig> {
    if (this.loading) return this.loading;
    this.loading = this.fetchConfig().then(cfg => {
      this.config.set(cfg);
      // A decision written by the landing page in another tab may have arrived meanwhile.
      this.reread();
      return cfg;
    });
    return this.loading;
  }

  /** Re-reads the cookies (e.g. after the landing page changed them in another tab). */
  reread(): void {
    const next = parseRecord(readCookie(CONSENT_COOKIE));
    const cur = this.record();
    if (JSON.stringify(next) !== JSON.stringify(cur)) this.record.set(next);
  }

  /**
   * Stores a decision. `action` defaults to what the choice amounts to compared to
   * the previous decision (see `actionFor`). Returns the written record.
   */
  grant(categories: readonly string[], surface: ConsentSurface, action?: ConsentAction): ConsentRecord {
    const cfg = this.effectiveConfig();
    const previous = this.record();
    const previousState = this.state();
    const c = normalizeCategories(categories, this.offeredCategories);
    const act = action ?? this.actionFor(previous, previousState, c);
    const record: ConsentRecord = {
      v: 1,
      // One consent id per browser record: a change or withdrawal keeps it, so the
      // backend can link every decision of this visitor (contract §4).
      id: previous && isUuidV4(previous.id) ? previous.id : consentUuid(),
      at: new Date().toISOString(),
      c,
      pv: cfg.policy_version
    };
    const maxAge = Math.max(1, Number(cfg.cookie_days) || 365) * 86400;
    writeCookie(CONSENT_COOKIE, encodeURIComponent(JSON.stringify(record)), maxAge);
    if (c.includes('analytics')) {
      const vid = readCookie(VISITOR_COOKIE);
      writeCookie(VISITOR_COOKIE, isUuidV4(vid) ? vid : consentUuid(), maxAge);
    } else {
      deleteCookie(VISITOR_COOKIE);
      deleteCookie(SESSION_COOKIE);
    }
    this.record.set(record);
    const state = consentStateOf(c);
    this.changeSubject.next({
      previous, previousState, record, state,
      action: act,
      surface, policyVersion: cfg.policy_version
    });
    return record;
  }

  /** Withdraws every optional category (preference center). */
  withdraw(surface: ConsentSurface = 'app_preferences'): ConsentRecord {
    return this.grant(['necessary'], surface, 'withdraw');
  }

  /** Called by a rendered settings link; returns its unregister function. */
  registerFooterLink(): () => void {
    this.footerLinks.update(n => n + 1);
    let done = false;
    return () => { if (!done) { done = true; this.footerLinks.update(n => Math.max(0, n - 1)); } };
  }

  openPreferences(): void { this.ready(); this.preferencesOpen.set(true); }
  closePreferences(): void { this.preferencesOpen.set(false); }

  /**
   * Visitor id from `wp_vid` (analytics only). A missing one is created — the
   * landing page may have been granted analytics without ever writing it.
   */
  visitorId(): string | null {
    if (!this.analyticsGranted()) return null;
    const vid = readCookie(VISITOR_COOKIE);
    if (isUuidV4(vid)) return vid;
    const fresh = consentUuid();
    writeCookie(VISITOR_COOKIE, fresh, Math.max(1, Number(this.effectiveConfig().cookie_days) || 365) * 86400);
    return fresh;
  }

  /**
   * - `withdraw`: a previously valid analytics/marketing grant loses a category;
   * - `reconfirm`: the policy version changed and the same categories are chosen again;
   * - otherwise what the choice is: all offered / necessary only / custom.
   */
  private actionFor(previous: ConsentRecord | null, previousState: ConsentState, c: ConsentCategory[]): ConsentAction {
    if (previousState === 'analytics' || previousState === 'marketing') {
      const lost = (previous?.c ?? []).some(x => !c.includes(x));
      if (lost) return 'withdraw';
    }
    if (previous && previous.v === 1 && previous.pv !== this.policyVersion && previousState === 'none' &&
        [...previous.c].sort().join() === [...c].sort().join()) {
      return 'reconfirm';
    }
    const offered = this.offeredCategories;
    if (offered.every(x => c.includes(x))) return 'accept_all';
    if (c.length === 1) return 'necessary_only';
    return 'custom';
  }

  private async fetchConfig(): Promise<ConsentRemoteConfig> {
    if (typeof fetch === 'undefined') return FALLBACK_CONSENT_CONFIG;
    let timer: ReturnType<typeof setTimeout> | null = null;
    try {
      const ctrl = typeof AbortController !== 'undefined' ? new AbortController() : null;
      if (ctrl) timer = setTimeout(() => ctrl.abort(), FETCH_TIMEOUT_MS);
      const res = await fetch(this.endpoint, { method: 'GET', headers: { Accept: 'application/json' }, signal: ctrl?.signal });
      if (!res.ok) return FALLBACK_CONSENT_CONFIG;
      const o: any = await res.json();
      const mode: PreConsentMode = o?.pre_consent_mode === 'cookieless' ? 'cookieless' : 'strict';
      const cats = Array.isArray(o?.categories)
        ? (o.categories as unknown[]).filter((x): x is ConsentCategory => (CONSENT_CATEGORIES as readonly unknown[]).includes(x))
        : [...CONSENT_CATEGORIES];
      if (!cats.includes('necessary')) cats.unshift('necessary');
      const master = typeof o?.master_language === 'string' && LANG_TAG.test(o.master_language.trim())
        ? o.master_language.trim() : 'en';
      return {
        config_domain_id: typeof o?.config_domain_id === 'number' ? o.config_domain_id : null,
        policy_version: typeof o?.policy_version === 'string' && o.policy_version ? o.policy_version : 'unknown',
        pre_consent_mode: mode,
        categories: cats,
        cookie_days: Number(o?.cookie_days) > 0 ? Number(o.cookie_days) : 365,
        policy_url: typeof o?.policy_url === 'string' ? o.policy_url : null,
        banner_languages: cleanLanguages(o?.banner_languages, master),
        master_language: master
      };
    } catch {
      return FALLBACK_CONSENT_CONFIG;
    } finally {
      if (timer) clearTimeout(timer);
    }
  }
}
